/* eslint-disable @typescript-eslint/no-explicit-any */
import { FunctionComponent, useMemo, useRef } from "react";
import { Editor } from "@tinymce/tinymce-react";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";

import { invalidPlaceholderStyle } from "@/utils/placeholderValidation";
import { decoratePlaceholderTokens, placeholderPillStyle } from "@/utils/placeholderPills";
import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { Icon } from "@/components";

import { getSuggestions } from "../../List/utils/column-utils";
import { EventOutreachSchemaType } from "../../List/schema/list";
import { getSuggestionLabel } from "../../List/components/CampaignOverview/utils/suggestions.utils";
import { stripHtmlToText } from "../../List/components/CampaignOverview/utils/stripHtmlToText";
import { ColumnConfig } from "../../List/schema/columnConfig";

export const SubjectEditor: FunctionComponent<{
	subject: string;
	handleChange: (value: string) => void;
	handleInputChange?: (value: boolean) => void;
	currentLeadIndex?: number;
	maxLength?: number;
	infoText?: string;
	placeholderSubject?: string;
	listColumns?: ColumnConfig[];
	listEvent?: EventOutreachSchemaType | null;
}> = ({
	subject,
	handleChange,
	currentLeadIndex = 0,
	maxLength,
	infoText,
	placeholderSubject,
	listColumns,
	listEvent,
}) => {
	const { inbox } = useAppSelector(listSelector);

	const editorRef = useRef<any>(null);
	const statusNodeRef = useRef<HTMLSpanElement | null>(null);

	const currentPerson = useMemo(() => {
		return inbox[currentLeadIndex];
	}, [currentLeadIndex, inbox]);

	const suggestions = useMemo(() => {
		return inbox[currentLeadIndex]
			? getSuggestions(inbox[currentLeadIndex], false, undefined, listEvent)
			: getSuggestions(undefined, true, listColumns, listEvent);
	}, [currentLeadIndex, inbox, listColumns, listEvent]);

	const processedSuggestions = useMemo(() => {
		return suggestions
			.map((key) => ({
				text: getSuggestionLabel(key, currentPerson, listColumns, listEvent),
				value: key,
			}))
			.filter((item) => item.text !== undefined);
	}, [suggestions, currentPerson, listColumns, listEvent]);

	const pillColumns = useMemo(
		() => listColumns ?? currentPerson?.list?.columns,
		[listColumns, currentPerson]
	);

	return processedSuggestions ? (
		<div className="relative flex flex-col gap-2">
			<Editor
				ref={editorRef}
				apiKey={import.meta.env.VITE_TINY_MCE} // optional for cloud features
				id="subject-editor"
				init={{
					type: "input",
					valid_classes: "mceNonEditable",
					menubar: false,
					height: 32,
					branding: false,
					resize: false,
					inline: true,
					br_in_pre: false,
					toolbar: false,
					placeholder: placeholderSubject?.trim() ? placeholderSubject : "Subject",
					extended_valid_elements:
						"span[data-placeholder|data-invalid|data-pill-type|title|data-mce-noneditable|class]",
					plugins: "lists paste code visualchars noneditable",
					// Keep the HTML simple & email-safe:
					forced_root_block: false, // avoid <div>
					valid_children: "+body[p]",
					noneditable_noneditable_class: "mceNonEditable",
					statusbar: true,
					// Only allow common email-safe tags/attrs:
					// URLs in email should be absolute:
					convert_urls: false,
					relative_urls: false,
					remove_script_host: false,
					paste_as_text: true,
					// Keep inline styles (fonts/colors) that email clients expect:
					invalid_styles: { "*": "margin" }, // strip margins (often ignored in email)
					uploadcare_public_key: "fcdffd80684e1dd9782f",
					content_css: "/custom-tinymce.css",
					paste_preprocess: (plugin: any, args: any) => {
						// args.content is HTML or text (since paste_as_text:true it's text)
						const text = args.content
							.replace(/\r?\n|\r/g, " ") // newlines -> space
							.replace(/<br\s*\/?>/gi, " ") // safety if any br slips in
							.replace(/<\/?p[^>]*>/gi, " "); // safety if any p slips ins

						args.content = text.replace(/\s{2,}/g, " "); // collapse double spaces
					},
					content_style: `
					${placeholderPillStyle}
					${invalidPlaceholderStyle}

            #subject-editor {
overflow: hidden;
    display: block;
    height: 32px;
    width: 100%;
    border-radius: 10px;
    border-width: 1px;
    border-style: solid;
    --tw-border-opacity: 1;
    border-color: rgb(229 231 235 / var(--tw-border-opacity));
    --tw-bg-opacity: 1;
    background-color: rgb(255 255 255 / var(--tw-bg-opacity));
    padding-left: 1rem;
    padding-right: 1rem;
    font-size: 1rem;
    line-height: 30px;
    --tw-text-opacity: 1;
    color: rgb(32 42 58 / var(--tw-text-opacity));
    transition-duration: 200ms;
  }
          `,

					setup: function (editor: any) {
						editor.on("BeforeSetContent", (e: any) => {
							if (!e.content) {
								return;
							}

							// Turn {first_name} into a pill span, **only** if not already wrapped
							e.content = decoratePlaceholderTokens(e.content, {
								processedSuggestions,
								columns: pillColumns,
							});
						});

						// 2) When content is read out of the editor
						editor.on("GetContent", (e: any) => {
							if (!e.content) {
								return;
							}

							// Replace spans back to {variable}
							e.content = e.content.replace(
								/<span[^>]*data-placeholder="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
								(match: string, varName: string) => `{${varName}}`
							);
						});

						editor.on("keydown", (e: KeyboardEvent) => {
							if (e.key === "Enter") {
								e.preventDefault();
							}
						});

						editor.ui.registry.addAutocompleter("variables", {
							trigger: "{", // ← use this (not `ch`)
							minChars: 0, // show immediately after `{`
							// default predicate requires start-of-word; allow anywhere:
							matches: () => true,
							fetch: (pattern: any) => {
								const list = processedSuggestions.filter((v) =>
									v.text.toLowerCase().includes(pattern.toLowerCase())
								);

								return Promise.resolve(
									list.map((v) => ({ type: "autocompleteitem", text: v.text, value: v.value }))
								);
							},
							onAction: (api: any, rng: any, value: any) => {
								editor.selection.setRng(rng);

								const text = processedSuggestions.find((v) => v.value === value);
								// If the next char is already '}', avoid duplicating it.
								const node = editor.selection.getNode();
								const contentAfter =
									node?.textContent?.slice(editor.selection.getRng().startOffset) ?? "";
								const needsClose = !contentAfter?.startsWith("}");

								editor.insertContent(
									needsClose ? "{" + value + "|" + (text?.text || value) + "}" : value
								);
								api.hide();
							},
						});

						const ensureStatusNode = () => {
							// Prefer the text container; fall back to full statusbar
							const container = editor.getContainer();
							const sb =
								container.querySelector(".tox-statusbar__text-container") ||
								container.querySelector(".tox-statusbar");

							if (sb && !statusNodeRef.current) {
								const el = document.createElement("span");

								el.className = "my-statusbar-left";
								el.textContent = "Type { to insert variables";
								// Put it at the start so it sits left of the element path/wordcount
								sb.prepend(el);
								statusNodeRef.current = el as HTMLSpanElement;
							}
						};

						// Run once the chrome is there; SkinLoaded covers theme load too
						editor.on("init SkinLoaded", ensureStatusNode);

						editor.on("remove", () => {
							statusNodeRef.current = null;
						});
					},
				}}
				initialValue={subject}
				onBlur={(e: any) => {
					let text = e.target.getContent();

					if (maxLength) {
						text = text.slice(0, maxLength);
					}

					text = text.replace(
						/<span[^>]*data-placeholder="([^"]+)"[^>]*>[\s\S]*?<\/span>/g,
						(match: string, name: string) => `{${name}}`
					);

					// Subjects are plain text — strip any remaining editor markup (e.g. <p>)
					// so the stored value (and the email we send) never carries block tags.
					// Runs after the pill→{name} conversion so variable tokens survive.
					text = stripHtmlToText(text);

					handleChange(text);
				}}
			/>
			{infoText && (
				<div className="text-xs text-text-disabled mt-0 mb-0">
					<Icon className="w-3 h-3 mr-1" icon={faInfoCircle} />
					{infoText}
				</div>
			)}
		</div>
	) : (
		<></>
	);
};
